import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useShippingQuote } from "@/lib/shipping";
import { lineTotal, lineOriginal } from "@/lib/pricing";
import { toast } from "sonner";
import { ArrowLeft, Lock, Spinner, ShoppingBag, Bank } from "@phosphor-icons/react";
import PaymentBadges from "@/components/PaymentBadges";

export default function Checkout() {
  const { items, clear } = useCart();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const shipMethod = params.get("ship") === "express" ? "express" : "standard";
  const nav = useNavigate();

  const lastTrackedRef = useRef("");
  const [email, setEmail] = useState(user?.email || "");
  const [addr, setAddr] = useState({ name: "", line1: "", line2: "", city: "", state: "", postal_code: "", phone: "" });
  const [cfg, setCfg] = useState({});
  const [manualLoading, setManualLoading] = useState("");
  const setA = (k, v) => setAddr((a) => ({ ...a, [k]: v }));

  const subtotalOriginal = items.reduce((s, i) => s + lineOriginal(i.price, i.quantity), 0);
  const total = items.reduce((s, i) => s + lineTotal(i.price, i.quantity), 0);
  const savings = subtotalOriginal - total;
  const quote = useShippingQuote(items, total);
  const freeThreshold = quote?.free_threshold ?? 180;
  const freeStandard = quote ? quote.standard === 0 : total >= freeThreshold;
  const shippingCost = quote ? quote[shipMethod] : 0;
  const grandTotal = total + shippingCost;

  useEffect(() => {
    api.get("/payments/config")
      .then((r) => setCfg(r.data || {}))
      .catch(() => {});
  }, []);

  const methods = [
    { key: "cashapp", label: "Pay with Cash App", recipient: cfg.cashapp_recipient },
    { key: "zelle", label: "Pay with Zelle", recipient: cfg.zelle_recipient },
    { key: "gpay", label: "Pay with Google Pay", recipient: cfg.gpay_recipient },
    { key: "applepay", label: "Pay with Apple Pay", recipient: cfg.applepay_recipient },
  ].filter((m) => m.recipient);

  function trackCart() {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || items.length === 0) return;
    if (lastTrackedRef.current === email) return;
    lastTrackedRef.current = email;
    api.post("/cart/track", { email, items: items.map((i) => ({ product_id: i.product_id, name: i.name, quantity: i.quantity })) }).catch(() => {});
  }

  async function payManual(method) {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email so we can match your transfer");
      return;
    }
    if (!addr.name.trim() || !addr.line1.trim() || !addr.city.trim() || !addr.state.trim() || !addr.postal_code.trim()) {
      toast.error("Please complete your shipping address");
      return;
    }
    setManualLoading(method);
    try {
      const { data } = await api.post("/orders/manual", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        shipping_method: shipMethod,
        buyer_email: email,
        method,
        shipping_address: addr,
      });
      clear();
      nav(`/payment/transfer?order_id=${data.order_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place your order");
      setManualLoading("");
    }
  }

  if (items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-32 text-center">
        <ShoppingBag size={48} className="text-muted-foreground mx-auto mb-6" />
        <h1 className="font-head text-3xl mb-3">Your cart is empty</h1>
        <p className="text-muted-foreground mb-8">Add some research compounds before checking out.</p>
        <Link to="/shop" className="inline-flex items-center gap-2 bg-primary text-black label-mono px-6 py-4">Browse Catalog</Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-16">
      <button onClick={() => nav("/cart")} data-testid="back-to-cart" className="inline-flex items-center gap-2 label-mono text-muted-foreground hover:text-primary transition-colors mb-8">
        <ArrowLeft size={16} /> Back to Cart
      </button>
      <h1 className="font-head text-4xl mb-12">Checkout</h1>

      <div className="grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-3 space-y-8">
          <div>
            <label className="label-mono text-muted-foreground block mb-3">Email for receipt</label>
            <input
              data-testid="checkout-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={trackCart}
              placeholder="you@example.com"
              className="w-full bg-white border border-black/15 px-4 py-3 font-mono text-sm text-black focus:border-primary outline-none transition-colors"
            />
          </div>

          <div>
            <span className="label-mono text-muted-foreground block mb-3">Shipping address</span>
            <div className="space-y-3">
              <input data-testid="ship-name" value={addr.name} onChange={(e) => setA("name", e.target.value)} placeholder="Full name"
                className="w-full bg-white border border-black/15 px-4 py-3 font-mono text-sm text-black focus:border-primary outline-none transition-colors" />
              <input data-testid="ship-line1" value={addr.line1} onChange={(e) => setA("line1", e.target.value)} placeholder="Street address"
                className="w-full bg-white border border-black/15 px-4 py-3 font-mono text-sm text-black focus:border-primary outline-none transition-colors" />
              <input data-testid="ship-line2" value={addr.line2} onChange={(e) => setA("line2", e.target.value)} placeholder="Apt, suite, unit (optional)"
                className="w-full bg-white border border-black/15 px-4 py-3 font-mono text-sm text-black focus:border-primary outline-none transition-colors" />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="ship-city" value={addr.city} onChange={(e) => setA("city", e.target.value)} placeholder="City"
                  className="w-full bg-white border border-black/15 px-4 py-3 font-mono text-sm text-black focus:border-primary outline-none transition-colors" />
                <input data-testid="ship-state" value={addr.state} onChange={(e) => setA("state", e.target.value)} placeholder="State"
                  className="w-full bg-white border border-black/15 px-4 py-3 font-mono text-sm text-black focus:border-primary outline-none transition-colors" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="ship-zip" value={addr.postal_code} onChange={(e) => setA("postal_code", e.target.value)} placeholder="ZIP code"
                  className="w-full bg-white border border-black/15 px-4 py-3 font-mono text-sm text-black focus:border-primary outline-none transition-colors" />
                <input data-testid="ship-phone" value={addr.phone} onChange={(e) => setA("phone", e.target.value)} placeholder="Phone (optional)"
                  className="w-full bg-white border border-black/15 px-4 py-3 font-mono text-sm text-black focus:border-primary outline-none transition-colors" />
              </div>
            </div>
          </div>

          <div>
            <span className="label-mono text-muted-foreground block mb-3">Choose how to pay</span>
            <div className="space-y-3">
              {methods.map((m, idx) => (
                <button
                  key={m.key}
                  data-testid={`pay-${m.key}-btn`}
                  onClick={() => payManual(m.key)}
                  disabled={!!manualLoading}
                  className={
                    idx === 0
                      ? "w-full flex items-center justify-center gap-2 bg-primary text-black label-mono py-4 hover:bg-[#E6C766] transition-colors disabled:opacity-50"
                      : "w-full flex items-center justify-center gap-2 border border-primary/40 text-foreground label-mono py-4 hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
                  }
                  style={idx === 0 ? { transition: "background-color 0.2s" } : { transition: "border-color 0.2s, color 0.2s" }}
                >
                  {manualLoading === m.key ? <><Spinner size={16} className="animate-spin" /> Placing order…</> : <><Bank size={18} /> {m.label}</>}
                </button>
              ))}
            </div>
          </div>

          <p className="label-mono text-muted-foreground text-[0.6rem] flex items-center gap-2">
            <Lock size={12} /> Your order is reserved — you'll get payment instructions and a reference next.
          </p>
        </div>

        <div className="lg:col-span-2 bg-surface border border-white/10 p-8 h-fit sticky top-24">
          <h2 className="font-head text-xl mb-6">Order Summary</h2>
          <div className="space-y-3 mb-6">
            {items.map((i) => (
              <div key={i.product_id} className="flex justify-between text-sm">
                <span className="text-muted-foreground">{i.quantity} × {i.name}</span>
                <span className="font-mono">${lineTotal(i.price, i.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="space-y-3 text-sm border-t border-white/10 pt-6">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">${subtotalOriginal.toFixed(2)}</span></div>
            {savings > 0 && (<div className="flex justify-between"><span className="text-primary">Bulk discount</span><span className="font-mono text-primary">−${savings.toFixed(2)}</span></div>)}
            <div className="flex justify-between"><span className="text-muted-foreground">Shipping ({shipMethod})</span><span className="font-mono">{!quote ? "…" : shippingCost === 0 ? "FREE" : `$${shippingCost.toFixed(2)}`}</span></div>
          </div>
          <div className="flex justify-between border-t border-white/10 mt-6 pt-6">
            <span className="font-head text-lg">Total</span>
            <span data-testid="checkout-total" className="font-mono text-2xl text-primary">${grandTotal.toFixed(2)}</span>
          </div>
          <div className="border-t border-white/10 mt-6 pt-6">
            <PaymentBadges />
          </div>
        </div>
      </div>
    </div>
  );
}
